import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';

export const toc = [
  { id: 'discovery', label: 'Discovery' },
  { id: 'search', label: 'Search' },
  { id: 'verify', label: 'Verify' },
  { id: 'export', label: 'Export (sandboxed)' },
  { id: 'premiere-control', label: 'Live Premiere control' },
  { id: 'skills', label: 'Bundled workflow skills' },
  { id: 'rules', label: 'The scoping & plan-first rules' },
];

type ToolRow = { name: string; desc: string };

function ToolTable({ rows }: { rows: ToolRow[] }) {
  return (
    <div
      style={{
        margin: '14px 0',
        border: '1px solid var(--line-2)',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--surface-card)',
      }}
    >
      {rows.map((r, i) => (
        <div
          key={r.name}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 220px) 1fr',
            gap: 14,
            padding: '10px 14px',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
          }}
        >
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--signal-300)', alignSelf: 'start' }}>
            {r.name}
          </code>
          <span style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-2)' }}>{r.desc}</span>
        </div>
      ))}
    </div>
  );
}

export default function Body() {
  return (
    <>
      <DocSection id="discovery" title="Discovery">
        <p>
          ClipRadar doesn't expose a public REST API — the programmatic surface is the{' '}
          <strong>Agent (MCP)</strong> server built into the desktop app (
          <span style={kbd}>Settings → Agent (MCP)</span>). Any MCP-capable agent (Claude Code,
          Claude Desktop, Codex, and others) connects to it locally and gets the tools on this
          page, grouped below by what they're for.
        </p>
        <Callout kind="tip">
          Start every session with discovery, in order:{' '}
          <code>check_status</code> → <code>load_analysis_data</code> → <code>list_media</code>.
          Nothing else in your library is visible to the agent until the indexes are loaded.
        </Callout>
        <ToolTable
          rows={[
            { name: 'check_status', desc: 'Health, license state, library counts, and whether the Premiere panel is connected.' },
            { name: 'load_analysis_data', desc: "Loads your visual, speech, and face indexes into memory. Run this once before any search." },
            { name: 'list_media', desc: 'Lists what is in your library — files, folders, collections.' },
            { name: 'read_skill', desc: 'Reads one of the bundled workflow skills (see below) before acting on a request that matches it.' },
            { name: 'get_skill_versions', desc: 'Checks which skill versions are installed.' },
          ]}
        />
      </DocSection>

      <DocSection id="search" title="Search">
        <p>
          Every search tool takes a <strong>scope</strong> — see{' '}
          <a href="#rules">the scoping rule</a> below — so an agent never accidentally searches
          your entire library when you meant one folder.
        </p>
        <ToolTable
          rows={[
            { name: 'visual_search', desc: 'Searches by what is on screen — a text description of the shot.' },
            { name: 'visual_search_by_frame', desc: 'Finds scenes similar to a specific frame already in your library.' },
            { name: 'visual_search_by_image', desc: 'Finds scenes similar to an external reference image.' },
            { name: 'ultra_search', desc: 'The fused search — visual description, spoken words, people, framing, and exclusions in one query, ranked with reciprocal rank fusion.' },
            { name: 'people_search', desc: 'Finds named people, optionally combined with a visual query; matches across several people with AND or ANY.' },
            { name: 'get_person_appearances', desc: 'Every scene a specific named person appears in.' },
            { name: 'identify_faces_in_frames', desc: 'Who is on screen at specific moments.' },
            { name: 'get_face_collections_for_media', desc: 'Which Face Collections a source belongs to.' },
            { name: 'list_people', desc: 'Every named person and cluster in your library.' },
            { name: 'list_face_collections', desc: 'The full list of Face Collections.' },
            { name: 'transcript_search', desc: 'Full-text keyword search over transcripts.' },
            { name: 'get_full_transcription', desc: 'The complete transcript for one source.' },
            { name: 'get_transcript_context', desc: 'The lines surrounding a transcript hit.' },
          ]}
        />
      </DocSection>

      <DocSection id="verify" title="Verify">
        <p>
          Before an agent exports or places anything, it's expected to actually look at what it
          found:
        </p>
        <ToolTable
          rows={[
            { name: 'get_thumbnail', desc: 'Saves a frame as a JPEG (or returns it inline) so the agent can inspect a hit before acting on it — the required step before export or timeline placement.' },
          ]}
        />
      </DocSection>

      <DocSection id="export" title="Export (sandboxed)">
        <p>
          Exports are sandboxed to a dedicated agent workspace folder plus your own{' '}
          <Link to="/docs/reference/where-your-data-lives">Export folder</Link> — never anywhere
          else on disk.
        </p>
        <ToolTable
          rows={[
            { name: 'export_clips', desc: 'Trimmed MP4 clips — fast mode (keyframe copy) or accurate mode (re-encoded to the exact in/out).' },
            { name: 'export_timeline_xml', desc: 'A timeline XML (Premiere XMEML) that imports into Premiere, DaVinci Resolve, or Final Cut — an import, not a live link.' },
            { name: 'export_transcript', desc: 'A transcript as txt, csv, or srt.' },
            { name: 'read_timeline_xml', desc: 'Reads an XML timeline back in.' },
          ]}
        />
      </DocSection>

      <DocSection id="premiere-control" title="Live Premiere control">
        <Callout kind="warn">
          These tools only work while the ClipRadar panel is open in Premiere Pro (
          <code>check_status</code> reports <code>panel_connected: true</code>). Without the panel
          connected, use the export tools above instead.
        </Callout>
        <ToolTable
          rows={[
            { name: 'write_nle_timeline', desc: 'Builds a timeline and imports it as a new sequence in your open project — the preferred way for an agent to deliver an edit.' },
            { name: 'read_nle_timeline', desc: 'Reads the active sequence.' },
            { name: 'insert_to_timeline', desc: 'Places one clip — Smart, Insert, Overwrite, or Append.' },
            { name: 'insert_batch_to_timeline', desc: 'Places several clips back-to-back.' },
            { name: 'navigate_to_timestamp', desc: 'Opens a moment in the Source Monitor.' },
            { name: 'mark_in_and_out', desc: 'Opens a clip in the Source Monitor with in/out already set.' },
            { name: 'check_media_in_project', desc: 'Checks whether a file is already in your open Premiere project.' },
          ]}
        />
        <p>
          See <Link to="/docs/concepts/agentic-editing">Agentic editing</Link> for how the panel
          connection works end to end.
        </p>
      </DocSection>

      <DocSection id="skills" title="Bundled workflow skills">
        <p>
          ClipRadar ships a set of workflow skills alongside the tools — read via{' '}
          <code>read_skill</code>, and also installable as standalone agent skills. They encode
          the search strategy for a specific kind of editing job, so an agent doesn't have to
          reinvent it every time:
        </p>
        <ToolTable
          rows={[
            { name: 'clipradar-general-search', desc: 'The baseline read before any search or export — required verification workflow and export guidance.' },
            { name: 'clipradar-vo-broll', desc: 'Voiceover + B-roll jobs: assembling a timeline of matching footage under narration audio.' },
            { name: 'clipradar-highlight-reel', desc: 'Planning a highlight reel, recap, or cut-down by selecting the best moments from longer footage.' },
            { name: 'clipradar-broll-finder', desc: 'Multi-round B-roll search for different sections of a script or sequence.' },
            { name: 'clipradar-transcript-analyzer', desc: 'Analyzing long transcripts to find key moments without bloating the conversation.' },
            { name: 'clipradar-face-cross-reference', desc: 'Cross-referencing people with transcripts to work out who says what, and where.' },
            { name: 'clipradar-join-in-finder', desc: 'Finding quotable spoken moments (soundbites) across a set of interviews and building a sequence from the picks.' },
            { name: 'clipradar-join-in-scout', desc: 'A sub-agent clipradar-join-in-finder dispatches to read full transcripts and shortlist candidates.' },
          ]}
        />
      </DocSection>

      <DocSection id="rules" title="The scoping & plan-first rules">
        <p>The MCP server enforces a few rules on every connected agent, not just suggestions:</p>
        <ul>
          <li>
            <strong>Plan-first.</strong> Discovery, then read the matching skill, then present a
            short plan and wait for your approval — only then does it search, export, or touch
            your timeline.
          </li>
          <li>
            <strong>Editor-intent heuristics.</strong> A person referred to by name always goes
            through <code>people_search</code>, never <code>visual_search</code>; a request for a
            "highlight reel" or "join-ins" reads the matching skill first instead of guessing at a
            search strategy.
          </li>
          <li>
            <strong>Scoping.</strong> Every search tool needs one of{' '}
            <code>media_paths</code>, <code>source_hashes</code>, or{' '}
            <code>search_all_media=true</code> — an agent can't search your whole library by
            accident.
          </li>
          <li>
            <strong>Verify before committing.</strong> Hits are inspected with thumbnails and
            non-matches dropped before export or timeline placement, never acted on blind.
          </li>
          <li>
            <strong>Sandboxed exports.</strong> Nothing lands outside the agent workspace and your
            own Export folder.
          </li>
        </ul>
      </DocSection>
    </>
  );
}
